<script setup>
import { ref, watch, onMounted } from "vue";
import VideoCallButton from "@/components/Common/VideoCallButton.vue";
import { EventManager, Actions, Events } from "@events";
import { useVideoCallState } from "@/stores/videoCallState";
import { ClientJS } from "clientjs";
import { sleep } from "@/utils";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

const state = useVideoCallState(props.rtcId);
let actionEmitter = null;
let eventEmitter = null;

// Agent Stuff
const isFlashBtnEnabled = ref(true);
const isFlashBtnVisible = ref(false);
const flashBtnIcon = ref("mdi-flashlight-off");
const isRemoteFlashTriggered = ref(false);

const toggleRemoteFlash = () => {
  isRemoteFlashTriggered.value = !isRemoteFlashTriggered.value;
  if (isRemoteFlashTriggered.value) {
    actionEmitter.emit(Actions.SendTextChannelMessage, {
      message: "FLASH_ON",
    });

    eventEmitter.emit(Events.CallEvent, { name: "FlashOn" });
  } else {
    actionEmitter.emit(Actions.SendTextChannelMessage, {
      message: "FLASH_OFF",
    });

    eventEmitter.emit(Events.CallEvent, { name: "FlashOff" });
  }
};

watch(isRemoteFlashTriggered, (value) => {
  if (value) {
    flashBtnIcon.value = "mdi-flashlight";
  } else {
    flashBtnIcon.value = "mdi-flashlight-off";
  }
});

// User Stuff
const clientJs = new ClientJS();
const localPeer = ref(null);

function sendFlashStatus(status = false) {
  actionEmitter.emit(Actions.SendTextChannelMessage, {
    message: status ? "FLASH_AVAILABLE" : "FLASH_NOT_AVAILABLE",
  });
}

// Main
onMounted(async () => {
  eventEmitter = EventManager.getEventEmitter(props.rtcId);
  actionEmitter = EventManager.getActionEmitter(props.rtcId);

  if (state.role == "agent") {
    // if agent
    eventEmitter.on(Events.TextChannelMessageReceived, async ({ data }) => {
      if (data.startsWith("FLASH_AVAILABLE")) {
        // If flash available then enable the flash trigger btn
        isFlashBtnEnabled.value = true;
      } else if (data.startsWith("FLASH_NOT_AVAILABLE")) {
        // If flash not available then disable the flash trigger btn
        isFlashBtnEnabled.value = false;
      } else if (data.startsWith("CAMERASTATUS|")) {
        const parts = data.split("|");
        if (parts[1] == "b") {
          // is camera swapped to back/rear camera?
          await sleep(1000); // wait for the camera to switch
          // then enable flash trigger btn
          isFlashBtnVisible.value = true;
        } else {
          // else disable flash trigger btn
          isFlashBtnVisible.value = false;

          // just in case if the flash triggered? then turn it off
          if (isRemoteFlashTriggered.value) {
            toggleRemoteFlash();
          }
        }
      } else if (data.startsWith("FLASH_STATE")) {
        let isFlashOn = data == "FLASH_STATE_ON";
        isRemoteFlashTriggered.value = isFlashOn;
      }
    });
  } else if (state.role == "user" && clientJs.isMobile()) {
    // If user is on mobile web
    localPeer.value = state.peers.find((peer) => peer.type === "local");

    // When switching camera to back camera // tell the agent ui about flash's status
    eventEmitter.on(Events.TextChannelMessageSent, async ({ data }) => {
      if (data.startsWith("CAMERASTATUS|")) {
        const parts = data.split("|");

        if (parts[1] == "b") {
          await sleep(1000); // wait for the camera to switch

          const rearCameraWithFlash = localPeer.value?.video?.srcObject?.getVideoTracks()[0];

          if (rearCameraWithFlash?.getCapabilities()?.torch) {
            sendFlashStatus(true);
          } else {
            sendFlashStatus(false);
          }
        }
      }
    });

    // When agent tells to turn on/off the flash remotely
    eventEmitter.on(Events.TextChannelMessageReceived, async ({ data }) => {
      if (data.startsWith("FLASH_ON")) {
        const rearCameraWithFlash = localPeer.value?.video?.srcObject?.getVideoTracks()[0];

        if (!rearCameraWithFlash?.getCapabilities()?.torch) {
          console.error(
            "Failed to start flash torch, rearCameraWithFlash does not support flash feature or it is not available.",
          );
          sendFlashStatus(false);
          return;
        }

        try {
          await rearCameraWithFlash.applyConstraints({
            advanced: [{ torch: true }],
          });
        } catch (error) {
          console.error("Error setting flashlight status:", error);
          sendFlashStatus(false);
        }
      } else if (data.startsWith("FLASH_OFF")) {
        const rearCameraWithFlash = localPeer.value?.video?.srcObject?.getVideoTracks()[0];

        if (!rearCameraWithFlash?.getCapabilities()?.torch) {
          console.error(
            "Failed to start flash torch, rearCameraWithFlash does not support flash feature or it is not available.",
          );
          sendFlashStatus(false);
          return;
        }

        try {
          await rearCameraWithFlash.applyConstraints({
            advanced: [{ torch: false }],
          });
        } catch (error) {
          console.error("Error setting flashlight status:", error);
          sendFlashStatus(false);
        }
      }
    });
  } else if (state.role == "user") {
    // if web user
    await sleep(10000);
    sendFlashStatus(false);
  }
});
</script>

<template>
  <VideoCallButton
    v-if="state.role == 'agent' && isFlashBtnVisible"
    :disabled="!isFlashBtnEnabled"
    :icon="flashBtnIcon"
    @click="toggleRemoteFlash" />
</template>
